import api from './api.service';

export interface BomLineDto {
  id: string;
  item_id: string;
  item_name: string;
  quantity: number;
  uom: string;
  scrap_percentage: number;
  notes?: string | null;
}

export interface BomOperationDto {
  id: string;
  operation_id: string;
  operation_code?: string | null;
  operation_name?: string | null;
  description?: string | null;
  sequence_number: number;
  setup_time_hours: number;
  run_time_per_unit_hours: number;
  teardown_time_hours: number;
  labor_rate_per_hour: number;
  overhead_rate_per_hour: number;
  machine_rate_per_hour: number;
  tooling_cost: number;
  is_outsourced: boolean;
  outsource_cost?: number | null;
  outsourced_scope_detail?: string | null;
  outsourced_supplier_category_ids?: string[] | null;
  requires_quality_check: boolean;
  notes?: string | null;
  total_cost: number;
}

export interface BomMasterDto {
  id: string;
  bom_code: string;
  bom_name: string;
  description?: string | null;
  item_id: string;
  item_name: string;
  quantity: number;
  uom: string;
  is_active: boolean;
  is_default: boolean;
  total_cost: number;
  material_cost?: number;
  operation_cost?: number;
  operation_count?: number;
  created_at?: string;
  items: BomLineDto[];
  operations?: BomOperationDto[];
}

export type BomLinkedWorkOrderRow = {
  id: string;
  work_order_number: string;
  work_order_status: string;
  planned_quantity: number;
  uom: string;
  item_code: string | null;
  item_name: string | null;
  link_type: 'bom_assigned' | 'same_finished_item' | 'wo_code_matches_bom_number';
};

export interface BomLinkedWorkOrdersPayload {
  work_orders: BomLinkedWorkOrderRow[];
}

export interface BomCreatePayload {
  bom_name: string;
  item_id: string;
  quantity: number;
  uom: string;
  is_active?: boolean;
  is_default?: boolean;
  description?: string | null;
  /** Required on create: sets work_orders.bom_id. */
  link_work_order_id?: string;
  /** Required on create: BOQ / estimate line (work order line item id). */
  link_work_order_line_item_id?: string;
  items: Array<{
    item_id: string;
    quantity: number;
    uom: string;
    scrap_percentage?: number;
    notes?: string | null;
  }>;
  operations?: Array<{
    operation_id: string;
    description?: string | null;
    sequence_number?: number;
    setup_time_hours?: number;
    run_time_per_unit_hours?: number;
    teardown_time_hours?: number;
    labor_rate_per_hour?: number;
    overhead_rate_per_hour?: number;
    machine_rate_per_hour?: number;
    tooling_cost?: number;
    is_outsourced?: boolean;
    outsource_cost?: number | null;
    outsourced_scope_detail?: string | null;
    outsourced_supplier_category_ids?: string[] | null;
    requires_quality_check?: boolean;
    notes?: string | null;
  }>;
}

const base = '/manufacturing/boms';

async function unwrap<T>(p: Promise<{ success?: boolean; data?: T; message?: string }>): Promise<T> {
  const res = await p;
  if (res.success === false || res.data === undefined) {
    throw new Error((res as { message?: string }).message || 'Request failed');
  }
  return res.data as T;
}

export const bomService = {
  async list(): Promise<BomMasterDto[]> {
    return unwrap(api.get<BomMasterDto[]>(base));
  },

  async getById(id: string): Promise<BomMasterDto> {
    return unwrap(api.get<BomMasterDto>(`${base}/${id}`));
  },

  async getLinkedWorkOrders(id: string): Promise<BomLinkedWorkOrdersPayload> {
    return unwrap(api.get<BomLinkedWorkOrdersPayload>(`${base}/${id}/linked-work-orders`));
  },

  async create(payload: BomCreatePayload): Promise<BomMasterDto> {
    return unwrap(api.post<BomMasterDto>(base, payload));
  },

  async update(id: string, payload: BomCreatePayload): Promise<BomMasterDto> {
    return unwrap(api.put<BomMasterDto>(`${base}/${id}`, payload));
  },

  async remove(id: string): Promise<void> {
    const res = await api.delete(`${base}/${id}`);
    if (res.success === false) {
      throw new Error(res.message || 'Delete failed');
    }
  },
};

export default bomService;
